import { useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import Magnetic from './Magnetic'

const LINKS = [
  { label: 'Home',     href: '/' },
  { label: 'Websites', href: '/websites' },
  { label: 'Hosting',  href: '/hosting' },
  { label: 'Projects', href: '/projects' },
  { label: 'About',    href: '/about' },
  { label: 'Contact',  href: '/contact' },
]

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false)
  const [open, setOpen]         = useState(false)
  const location                = useLocation()

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50)
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  useEffect(() => { setOpen(false) }, [location.pathname])

  return (
    <>
      {/* Nav wrapper — full width transparent, floating pill inner */}
      <nav style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1000,
        padding: scrolled ? '10px 20px' : '16px 20px',
        transition: 'padding 0.4s cubic-bezier(.22,1,.36,1)',
        pointerEvents: 'none',
      }}>
        <div style={{
          maxWidth: 1100,
          margin: '0 auto',
          height: 46,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderRadius: 14,
          padding: '0 20px',
          pointerEvents: 'all',
          transition: 'background 0.4s ease, border-color 0.4s ease, box-shadow 0.4s ease',
          ...(scrolled ? {
            background: 'rgba(0,0,0,0.75)',
            backdropFilter: 'blur(28px)',
            WebkitBackdropFilter: 'blur(28px)',
            border: '1px solid rgba(255,255,255,0.06)',
            boxShadow: '0 8px 40px rgba(0,0,0,0.45), 0 0 0 1px rgba(6,182,212,0.04), inset 0 1px 0 rgba(255,255,255,0.04)',
          } : {
            background: 'transparent',
            border: '1px solid transparent',
          }),
        }}>

          {/* Logo */}
          <Link to="/" style={{ display: 'flex', alignItems: 'center', textDecoration: 'none', flexShrink: 0 }}>
            <span style={{ color: '#fff', fontWeight: 600, fontSize: 18, fontFamily: 'Geist Variable, sans-serif', letterSpacing: '-0.02em' }}>
              CJM <span style={{ color: '#22d3ee' }}>Studios</span>
            </span>
          </Link>

          {/* Desktop nav */}
          <div className="nav-desktop" style={{ alignItems: 'center', gap: 2 }}>
            {LINKS.map((link) => {
              const active = location.pathname === link.href
              return (
                <Link
                  key={link.href}
                  to={link.href}
                  style={{
                    padding: '7px 13px',
                    borderRadius: 999,
                    fontSize: 13,
                    fontWeight: 500,
                    textDecoration: 'none',
                    color: active ? '#fff' : 'rgba(255,255,255,0.4)',
                    background: active ? 'rgba(255,255,255,0.08)' : 'transparent',
                    transition: 'color 0.2s, background 0.2s',
                    letterSpacing: '-0.01em',
                    position: 'relative',
                  }}
                  onMouseEnter={(e) => {
                    if (!active) {
                      e.currentTarget.style.color = '#fff'
                      e.currentTarget.style.background = 'rgba(255,255,255,0.05)'
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!active) {
                      e.currentTarget.style.color = 'rgba(255,255,255,0.4)'
                      e.currentTarget.style.background = 'transparent'
                    }
                  }}
                >
                  {link.label}
                  {active && (
                    <span style={{
                      position: 'absolute', bottom: 2, left: '50%',
                      transform: 'translateX(-50%)',
                      width: 4, height: 4, borderRadius: '50%',
                      background: '#06b6d4',
                      boxShadow: '0 0 8px rgba(6,182,212,0.8)',
                    }} />
                  )}
                </Link>
              )
            })}
          </div>

          {/* Desktop CTA */}
          <div className="nav-desktop">
            <Magnetic strength={0.18}>
              <Link to="/contact" className="btn-primary" style={{ padding: '9px 20px', fontSize: 13 }}>
                <span>Start a Project</span>
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                  <path d="M5 12h14M12 5l7 7-7 7" />
                </svg>
              </Link>
            </Magnetic>
          </div>

          {/* Mobile toggle */}
          <button
            className="nav-mobile-toggle"
            onClick={() => setOpen(!open)}
            style={{
              background: 'none', border: 'none', color: 'rgba(255,255,255,0.65)',
              padding: 8, alignItems: 'center', justifyContent: 'center',
            }}
            aria-label="Toggle menu"
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 5, width: 22 }}>
              <span style={{
                display: 'block', height: 1.5, borderRadius: 2,
                background: 'rgba(255,255,255,0.7)',
                transform: open ? 'translateY(6.5px) rotate(45deg)' : 'none',
                transition: 'transform 0.3s cubic-bezier(.22,1,.36,1)',
              }} />
              <span style={{
                display: 'block', height: 1.5, borderRadius: 2,
                background: 'rgba(255,255,255,0.7)',
                opacity: open ? 0 : 1,
                transition: 'opacity 0.2s',
              }} />
              <span style={{
                display: 'block', height: 1.5, borderRadius: 2,
                background: 'rgba(255,255,255,0.7)',
                transform: open ? 'translateY(-6.5px) rotate(-45deg)' : 'none',
                transition: 'transform 0.3s cubic-bezier(.22,1,.36,1)',
              }} />
            </div>
          </button>
        </div>
      </nav>

      {/* Mobile menu */}
      <div style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 999,
        background: 'rgba(0,0,0,0.96)',
        backdropFilter: 'blur(28px)',
        WebkitBackdropFilter: 'blur(28px)',
        padding: '100px 32px 32px',
        transition: 'opacity 0.3s ease, transform 0.3s cubic-bezier(.22,1,.36,1)',
        opacity: open ? 1 : 0,
        transform: open ? 'translateY(0)' : 'translateY(-16px)',
        pointerEvents: open ? 'all' : 'none',
      }} className="nav-mobile-menu">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {LINKS.map((link) => (
            <Link
              key={link.href}
              to={link.href}
              style={{
                display: 'block',
                padding: '18px 0',
                borderBottom: '1px solid rgba(255,255,255,0.05)',
                fontSize: 24,
                fontWeight: 500,
                textDecoration: 'none',
                letterSpacing: '-0.02em',
                color: location.pathname === link.href ? '#22d3ee' : 'rgba(255,255,255,0.55)',
                fontFamily: 'Geist Variable, sans-serif',
                transition: 'color 0.2s',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.color = '#fff' }}
              onMouseLeave={(e) => { e.currentTarget.style.color = location.pathname === link.href ? '#22d3ee' : 'rgba(255,255,255,0.55)' }}
            >
              {link.label}
            </Link>
          ))}
        </div>
        <Link to="/contact" className="btn-primary" style={{ marginTop: 32, display: 'flex', justifyContent: 'center' }}>
          <span>Start a Project</span>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            <path d="M5 12h14M12 5l7 7-7 7" />
          </svg>
        </Link>
      </div>

      <style>{`
        .nav-desktop { display: none; }
        .nav-mobile-toggle { display: flex; }
        @media(min-width: 768px) {
          .nav-desktop { display: flex; }
          .nav-mobile-toggle { display: none; }
          .nav-mobile-menu { display: none !important; }
        }
        @media(max-width: 480px) {
          nav { padding-left: 12px !important; padding-right: 12px !important; }
        }
      `}</style>
    </>
  )
}
